import React from "react";
import { makeStyles, type Theme } from "@material-ui/core/styles";
import { useDispatch, useSelector } from "react-redux";
import {
  selectSearchCriteria,
  selectNegocio,
  selectEstado,
  selectFechaDesde,
  selectFechaHasta,
} from "./actions";
import { setPage, setTake } from "../Filtros/actions";
import { calculateDate } from "~libs/dateCalculator";
import Searcher from "~styled/searcher/searcher";
import Selector, { type OptionKV } from "~styled/selector/Selector";
import { negociosFilter, negociosSegurosOptions } from "~constants/negocios";
import { Typography } from "@material-ui/core";
import Pagination from "~styled/pagination/Pagination";
import DateRangePicker from "~styled/dateRangePicker/DateRangePicker";
import { queueMessage } from "~shared/snackbarsProvider/actions";
import { paginationOptions } from "~constants/pagination";
import {
  estadosMulticotizacionOptions,
  estadosMulticotizacionFilter,
} from "~constants/multicotizacionEstados";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "flex-start",
    paddingBottom: 20,
    width: "100%",
  },
  filtersContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  filters: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    paddingBottom: "1%",
  },
  label: {
    fontSize: "10px",
    fontWeight: 500,
    fontStyle: "normal",
    fontStretch: "normal",
    lineHeight: "1.3",
    letterSpacing: "normal",
    textAlign: "left",
    color: theme.colours.grey2,
    paddingLeft: 5,
    paddingBottom: 5,
  },
  dateSelectorAndTitle: {
    display: "flex",
    flexDirection: "column",
    minWidth: 250,
  },
  horizontalSpace: { paddingRight: "10px", minWidth: 250 },
  horizontalSpaceSearcher: { paddingRight: "10px", minWidth: 250 },
  textItemContainer: { display: "flex", flexDirection: "column" },
}));

type NegocioValue = string | number;
type EstadoValue = string | number;
type RowsValue = string | number;

interface FiltrosSlice {
  isLoading: boolean;
  totalRegistros: number;
  take: RowsValue;
  page: number;
  fechaDesde: Date | string | null;
  fechaHasta: Date | string | null;
  searchCriteria: string;
  negocio: NegocioValue;
  estado: EstadoValue;
}

const useReduxSelector = () =>
  useSelector<any, FiltrosSlice>((state) => ({
    isLoading: state.cotizacionesReducer.isLoading,
    totalRegistros: state.cotizacionesReducer.totalRegistros,
    take: state.filtrosCotizacionesReducer.take,
    page: state.filtrosCotizacionesReducer.page,
    fechaDesde: state.filtrosCotizacionesReducer.fechaDesde,
    fechaHasta: state.filtrosCotizacionesReducer.fechaHasta,
    searchCriteria: state.filtrosCotizacionesReducer.searchCriteria,
    negocio: state.filtrosCotizacionesReducer.negocio,
    estado: state.filtrosCotizacionesReducer.estado,
  }));

const messageLoading = "Cargando su consulta previa" as const;

const Filtros: React.FC = () => {
  const {
    isLoading,
    take,
    fechaDesde,
    fechaHasta,
    searchCriteria,
    negocio,
    estado,
    page,
    totalRegistros,
  } = useReduxSelector();

  const dispatch = useDispatch();

  const handleChange = <T,>(
    event: { target: { value: T } },
    action: (v: T) => any
  ) => {
    dispatch(setPage(0));
    dispatch(action(event.target.value));
  };

  const handleChangeNegocio = (value: NegocioValue) => {
    dispatch(setPage(0));
    dispatch(selectNegocio(value));
  };

  const handleChangeEstado = (value: EstadoValue) => {
    dispatch(setPage(0));
    dispatch(selectEstado(value));
  };

  const handleChangeInterval = (value: number | null) => {
    dispatch(setPage(0));
    if (value == null) return;
    dispatch(selectFechaDesde(calculateDate(new Date(), value)));
    dispatch(selectFechaHasta(new Date()));
  };

  const handleChangePage = (value: number) => {
    dispatch(setPage(value));
  };

  const classes = useStyles();

  const negocioLabelMap = negociosSegurosOptions as Record<string, string>;
  const estadoLabelMap = estadosMulticotizacionOptions as Record<
    string,
    string
  >;

  const paginationOptionsStr = React.useMemo(
    () =>
      paginationOptions.map((o) => ({
        label: String(o.label),
        value: o.value,
      })),
    [paginationOptions]
  );

  return (
    <div className={classes.root}>
      <>
        <div className={classes.filtersContainer}>
          <div className={classes.filters}>
            <div className={classes.textItemContainer}>
              <Typography variant="h3" className={classes.label}>
                BUSCAR
              </Typography>
              <div className={classes.horizontalSpaceSearcher}>
                <Searcher
                  borderSquared
                  value={searchCriteria}
                  filter={searchCriteria}
                  placeholder="Vehículo, Nombre/Apellido, Dominio"
                  onChange={(event) =>
                    handleChange(event, selectSearchCriteria)
                  }
                  onClear={() =>
                    handleChange(
                      { target: { value: "" } },
                      selectSearchCriteria
                    )
                  }
                />
              </div>
            </div>

            <div className={classes.dateSelectorAndTitle}>
              <Typography variant="h3" className={classes.label}>
                FECHA DE INGRESO
              </Typography>
              <div className={classes.horizontalSpace}>
                <DateRangePicker
                  fechaDesde={fechaDesde}
                  fechaHasta={fechaHasta}
                  onChangeInterval={handleChangeInterval}
                  onChangeStartDate={(value: Date | string) =>
                    handleChange({ target: { value } }, selectFechaDesde)
                  }
                  onChangeEndDate={(value: Date | string) =>
                    handleChange({ target: { value } }, selectFechaHasta)
                  }
                  onClickDisabled={() => {
                    dispatch(queueMessage(messageLoading));
                  }}
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className={classes.textItemContainer}>
              <Typography variant="h3" className={classes.label}>
                NEGOCIO
              </Typography>

              <div className={classes.horizontalSpace}>
                <Selector<NegocioValue>
                  value={negocioLabelMap[String(negocio)]}
                  options={negociosFilter as OptionKV<NegocioValue>[]}
                  onChange={handleChangeNegocio}
                  onClickDisabled={() => {
                    dispatch(queueMessage(messageLoading));
                  }}
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>
        </div>

        <div className={classes.filtersContainer}>
          <div className={classes.filters}>
            <div className={classes.textItemContainer}>
              <Typography variant="h3" className={classes.label}>
                ESTADO
              </Typography>

              <div className={classes.horizontalSpace}>
                <Selector<EstadoValue>
                  value={estadoLabelMap[String(estado)]}
                  options={
                    estadosMulticotizacionFilter as OptionKV<EstadoValue>[]
                  }
                  onChange={handleChangeEstado}
                  onClickDisabled={() => {
                    dispatch(queueMessage(messageLoading));
                  }}
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>

          {!isLoading && (
            <div className={classes.filters}>
              <Pagination<RowsValue>
                rowsPerPage={take}
                onChangeRowsPerPage={(value) =>
                  handleChange(
                    { target: { value } },
                    setTake as (v: RowsValue) => any
                  )
                }
                page={page}
                onChangePage={handleChangePage}
                totalRegistros={totalRegistros}
                options={paginationOptionsStr}
              />
            </div>
          )}
        </div>
      </>
      <></>
    </div>
  );
};

export default Filtros;
